import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Activity, ShieldCheck } from "lucide-react";
import { Icon, MaskLine, EASE } from "./primitives";
import { useScrollTo } from "./scroll";
import { openDemoChooser } from "./DemoBookingModal";

const MINI = [
  { icon: "Hospital", label: "Hospital", tone: "health", col: "lg:col-start-1 lg:row-start-1" },
  { icon: "FlaskConical", label: "Laboratory", tone: "health", col: "lg:col-start-3 lg:row-start-1" },
  { icon: "Pill", label: "Pharmacy", tone: "health", col: "lg:col-start-1 lg:row-start-3" },
  { icon: "GraduationCap", label: "School", tone: "edu", col: "lg:col-start-3 lg:row-start-3" },
];

const MiniCard = ({ icon, label, tone, delay }) => {
  const color = tone === "health" ? "#0D9488" : "#4F46E5";
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.7, ease: EASE, delay }}
      className="rounded-2xl border border-black/5 bg-white/90 p-4 shadow-[0_10px_40px_rgb(11,19,43,0.08)] backdrop-blur"
    >
      <div className="flex items-center gap-2.5">
        <span
          className="flex h-9 w-9 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${color}14`, color }}
        >
          <Icon name={icon} className="h-4.5 w-4.5" size={18} />
        </span>
        <span className="text-sm font-semibold text-ajmus-ink">{label}</span>
      </div>
      <div className="mt-3 space-y-1.5">
        <div className="h-1.5 w-full rounded-full bg-ajmus-navy/8" />
        <div className="h-1.5 w-3/4 rounded-full bg-ajmus-navy/8" />
      </div>
    </motion.div>
  );
};

function PlatformVisual() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [30, -30]);

  return (
    <motion.div ref={ref} style={{ y }} className="relative mx-auto w-full max-w-[520px]">
      {/* connecting lines */}
      <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {["20,20 50,50", "80,20 50,50", "20,80 50,50", "80,80 50,50"].map((pts, i) => (
          <polyline
            key={i}
            points={pts}
            fill="none"
            stroke="url(#lineGrad)"
            strokeWidth="0.5"
            className="animate-pulse-line"
            style={{ animationDelay: `${i * 0.4}s` }}
          />
        ))}
        <defs>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0D9488" />
            <stop offset="100%" stopColor="#4F46E5" />
          </linearGradient>
        </defs>
      </svg>

      <div className="relative grid grid-cols-1 gap-4 lg:grid-cols-3 lg:grid-rows-3">
        {MINI.map((m, i) => (
          <div key={m.label} className={m.col}>
            <MiniCard {...m} delay={0.5 + i * 0.12} />
          </div>
        ))}

        {/* Core node */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.35 }}
          className="lg:col-start-2 lg:row-start-2 lg:self-center"
        >
          <div className="rounded-2xl border border-white/10 bg-ajmus-navy p-5 text-white shadow-[0_20px_60px_rgb(11,19,43,0.35)]">
            <div className="flex items-center justify-between">
              <span className="font-heading text-sm font-extrabold tracking-tight">AJMUS Core</span>
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-white/10">
                <Activity size={14} className="text-health-soft" />
              </span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-1.5">
              {["HR", "Reports", "Billing", "Ops", "Comms", "Roles"].map((t) => (
                <span key={t} className="rounded-md bg-white/8 px-1.5 py-1 text-center text-[9px] font-medium text-white/75">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* floating stat chips */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE, delay: 1 }}
        className="absolute -right-3 top-1/2 hidden animate-float-slow items-center gap-2 rounded-full border border-black/5 bg-white px-3 py-2 shadow-lg sm:flex"
      >
        <ShieldCheck size={15} className="text-health" />
        <span className="text-xs font-semibold text-ajmus-ink">Role-based access</span>
      </motion.div>
    </motion.div>
  );
}

export default function Hero() {
  const scrollToId = useScrollTo();
  return (
    <section id="home" className="relative overflow-hidden bg-[#FAFAFA] pt-[72px]">
      {/* ambient background */}
      <div className="pointer-events-none absolute inset-0 grid-lines mask-fade-y opacity-70" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-40 top-10 h-[420px] w-[420px] rounded-full bg-health/10 blur-[120px]" aria-hidden="true" />
      <div className="pointer-events-none absolute -right-40 top-40 h-[460px] w-[460px] rounded-full bg-edu/10 blur-[130px]" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 py-20 sm:px-8 lg:grid-cols-2 lg:gap-10 lg:py-28">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-black/5 bg-white px-3.5 py-1.5 shadow-sm"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-health" />
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ajmus-steel">
              Healthcare &amp; Education Software
            </span>
          </motion.div>

          <h1 className="mt-6 font-heading text-[2.6rem] font-extrabold leading-[1.02] tracking-tight text-ajmus-ink sm:text-6xl lg:text-[4rem]">
            <MaskLine delay={0.15}>One Intelligent</MaskLine>
            <MaskLine delay={0.28}>Platform for</MaskLine>
            <MaskLine delay={0.41}>
              <span className="bg-gradient-to-r from-health via-ajmus-royal to-edu bg-clip-text text-transparent">
                Healthcare &amp; Education
              </span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.6 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-ajmus-steel sm:text-lg"
          >
            AJMUS helps hospitals, clinics, diagnostic laboratories, pharmacies, schools and
            educational institutions manage their complete operations through connected, reliable
            and easy-to-use software.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.72 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              onClick={openDemoChooser}
              data-testid="hero-demo-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-ajmus-navy px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-black"
            >
              Live Demo
              <ArrowUpRight size={17} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={() => scrollToId("healthcare")}
              data-testid="hero-explore-btn"
              className="inline-flex items-center gap-2 rounded-full border border-ajmus-navy/15 bg-white px-6 py-3.5 text-sm font-semibold text-ajmus-navy transition-colors duration-200 hover:bg-ajmus-navy/5"
            >
              Explore Our Solutions
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.9 }}
            className="mt-6 text-sm text-ajmus-steel"
          >
            Built to simplify operations, improve visibility, and support better decisions.
          </motion.p>
        </div>

        <div className="relative">
          <PlatformVisual />
        </div>
      </div>
    </section>
  );
}
